import { components, OptionProps, MultiValueGenericProps } from 'react-select';
import { ComponentProps } from 'react';

import { Registry } from '@/react/portainer/registries/types/registry';

import { Select } from '@@/form-components/ReactSelect';
import { FormControl } from '@@/form-components/FormControl';
import { Tooltip } from '@@/Tip/Tooltip';

interface Props {
  value: Registry[];
  onChange(value: readonly Registry[]): void;
  options: Registry[];
  label?: string;
  placeholder?: string;
  emptyText?: string;
  inputId?: string;
  dataCy?: string;
  showFormControl?: boolean;
  showEmptyState?: boolean;
  tooltip?: ComponentProps<typeof Tooltip>['message'];
  setTooltipHtmlMessage?: ComponentProps<typeof Tooltip>['setHtmlMessage'];
  errors?: ComponentProps<typeof FormControl>['errors'];
}

export function MultiRegistrySelectFieldset({
  options,
  value,
  onChange,
  label = 'Select registries',
  placeholder = 'Select one or more registries',
  emptyText = 'No registries available.',
  inputId = 'registries-selector',
  dataCy = 'component-selectRegistry',
  showFormControl = true,
  showEmptyState = true,
  tooltip,
  setTooltipHtmlMessage,
  errors,
}: Props) {
  function handleChange(newValue: readonly Registry[]) {
    try {
      onChange(newValue);
    } catch {
      // Error handling without console output
    }
  }

  const selectComponent = (
    <Select
      isMulti
      getOptionLabel={(option) => option.Name}
      getOptionValue={(option) => option.Id.toString()}
      options={options}
      value={value}
      closeMenuOnSelect={false}
      onChange={handleChange}
      data-cy={dataCy}
      id={dataCy}
      inputId={inputId}
      placeholder={placeholder}
      components={{ MultiValueLabel, Option: OptionComponent }}
    />
  );

  // If no FormControl wrapper is needed, return just the Select
  if (!showFormControl) {
    return selectComponent;
  }

  // If showing empty state and no options, show empty text
  if (showEmptyState && options.length === 0) {
    return (
      <FormControl
        label={
          <>
            {label}
            {tooltip && (
              <Tooltip
                message={tooltip}
                setHtmlMessage={setTooltipHtmlMessage}
              />
            )}
          </>
        }
        inputId={inputId}
      >
        <span className="small text-muted">{emptyText}</span>
      </FormControl>
    );
  }

  // Return with FormControl wrapper
  return (
    <FormControl
      label={
        <>
          {label}
          {tooltip && (
            <Tooltip message={tooltip} setHtmlMessage={setTooltipHtmlMessage} />
          )}
        </>
      }
      inputId={inputId}
      errors={errors}
    >
      {selectComponent}
    </FormControl>
  );
}

function isRegistry(option: unknown): option is Registry {
  return (
    !!option && typeof option === 'object' && 'Id' in option && 'Name' in option
  );
}

function OptionComponent({ data, ...props }: OptionProps<Registry, true>) {
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <components.Option data={data} {...props}>
      {isRegistry(data) && <RegistryLabel registry={data} />}
    </components.Option>
  );
}

function MultiValueLabel({
  data,
  ...props
}: MultiValueGenericProps<Registry, true>) {
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <components.MultiValueLabel data={data} {...props}>
      {isRegistry(data) && <RegistryLabel registry={data} />}
    </components.MultiValueLabel>
  );
}

function RegistryLabel({ registry }: { registry: Registry }) {
  return (
    <div className="flex items-center gap-1">
      <span className="font-medium">{registry.Name}</span>
      {registry.URL && (
        <span className="text-muted text-xs">({registry.URL})</span>
      )}
    </div>
  );
}
